/**
 * Per-kind manifest schemas.
 *
 * Each schema validates the manifest sidecar that accompanies an artifact of
 * the matching `ArtifactKind`. CRS fields (`crs`, `crsIn`, `crsOut`,
 * `chainageCrs`) are preserved EXACTLY from the converter source per the
 * contract matrix (`04-converter-viewer-contract-matrix.md`).
 */

import { z } from "zod";
import { ArtifactKind } from "./handoff.js";

// ---------------------------------------------------------------------------
// 2D map manifests (pmtiles, cog, geojson, flatgeobuf, geoparquet, dem-contours, dxf)
// ---------------------------------------------------------------------------

/** Manifest for raster + vector 2D map artifacts. */
export const Map2DManifestSchema = z.object({
  kind: z.string(),
  url: z.string(),
  manifestUrl: z.string().nullable().optional(),
  crs: z.string().nullable().optional(),
  bounds: z
    .tuple([z.number(), z.number(), z.number(), z.number()])
    .nullable()
    .optional(),
  featureCount: z.number().nullable().optional(),
  attributes: z.unknown().nullable().optional(),
  tileCount: z.number().nullable().optional(),
  archiveSizeBytes: z.number().nullable().optional(),
});
export type Map2DManifest = z.infer<typeof Map2DManifestSchema>;

// ---------------------------------------------------------------------------
// 3D manifests (glb, 3dtiles, cityjson-3dtiles, quantized-mesh, copc, potree-v2-octree)
// ---------------------------------------------------------------------------

/** Manifest for 3D scene + point cloud + terrain artifacts. */
export const ThreeDManifestSchema = z.object({
  kind: z.string(),
  url: z.string().nullable().optional(),
  tilesetUrl: z.string().nullable().optional(),
  glbUrl: z.string().nullable().optional(),
  layerJsonUrl: z.string().nullable().optional(),
  metadataUrl: z.string().nullable().optional(),
  geometricError: z.number().nullable().optional(),
  bbox: z.array(z.number()).nullable().optional(),
  lodCount: z.number().nullable().optional(),
  pointCount: z.number().nullable().optional(),
  classifications: z.array(z.number()).nullable().optional(),
  dimensions: z.array(z.string()).nullable().optional(),
  colorModes: z.array(z.string()).nullable().optional(),
  potreeVersion: z.string().nullable().optional(),
  transform: z.array(z.number()).nullable().optional(),
});
export type ThreeDManifest = z.infer<typeof ThreeDManifestSchema>;

// ---------------------------------------------------------------------------
// Profile manifests (dem-contours profile cross-section overlay)
// ---------------------------------------------------------------------------

/** Manifest for 1D long-section / profile overlays (DEM contours, etc.). */
export const ProfileManifestSchema = z.object({
  kind: z.string(),
  contourInterval: z.number().nullable().optional(),
  stationUnit: z.string().nullable().optional(),
  elevationUnit: z.string().nullable().optional(),
  elevationDatum: z.string().nullable().optional(),
  chainageCrs: z.string().nullable().optional(),
  stations: z
    .array(
      z.object({
        station: z.number(),
        elevation: z.number(),
        anomaly: z.string().nullable().optional(),
        toleranceState: z
          .enum(["within", "approaching", "breached"])
          .nullable()
          .optional(),
      }),
    )
    .optional(),
  tolerance: z.object({ low: z.number(), high: z.number() }).nullable().optional(),
});
export type ProfileManifest = z.infer<typeof ProfileManifestSchema>;

// ---------------------------------------------------------------------------
// Panorama manifests (panorama-pose-v1)
// ---------------------------------------------------------------------------

/** Manifest for panorama-pose-v1 artifacts (street-level / inspection). */
export const PanoramaManifestSchema = z.object({
  kind: z.literal("panorama-pose-v1"),
  equirectUrl: z.string(),
  poseUrl: z.string(),
  metadataUrl: z.string().nullable().optional(),
  headingConvention: z.string().default("cesium-east-north-up"),
  aspectRatio: z.tuple([z.number(), z.number()]).nullable().optional(),
  minWidth: z.number().nullable().optional(),
  exifStripped: z.boolean().nullable().optional(),
  // `domain-coverage.md` §4.13 + Round-2 user spec — broadened source values:
  // `sidecar` > `xmp+exif` > `exif-only`. Legacy `gpano` / `exif` accepted for
  // backward-compatibility (older converters emit those tokens).
  poseSource: z
    .enum(["sidecar", "xmp+exif", "exif-only", "gpano", "exif"])
    .nullable()
    .optional(),
  pose: z
    .object({
      heading: z.number(),
      pitch: z.number().default(0),
      roll: z.number().default(0),
      // WGS-84 geographic position. Optional because some panoramas ship
      // without geographic anchoring.
      lat: z.number().nullable().optional(),
      lon: z.number().nullable().optional(),
      alt: z.number().nullable().optional(),
    })
    .nullable()
    .optional(),
  poseSourceConflict: z.boolean().nullable().optional(),
  // BL-0015 diagnostics array (subset). Used by the viewer to surface a
  // `pose_source_conflict` chip + 1-line explanation when present.
  diagnostics: z
    .array(
      z.object({
        code: z.string(),
        severity: z.enum(["info", "warning", "error", "blocking"]).optional(),
        message: z.string().nullable().optional(),
      }),
    )
    .nullable()
    .optional(),
});
export type PanoramaManifest = z.infer<typeof PanoramaManifestSchema>;

// ---------------------------------------------------------------------------
// Panorama bubble manifests (panorama-bubble-v1) — SPEC §3–§4
// ---------------------------------------------------------------------------

const vec3Schema = z.tuple([z.number(), z.number(), z.number()]);
const quaternionSchema = z.tuple([z.number(), z.number(), z.number(), z.number()]);

/** Tile pyramid descriptor (SPEC §2.1, §4). */
export const PanoramaBubbleTilePyramidSchema = z.object({
  tileSize: z.number().int().positive(),
  levels: z.number().int().positive(),
  format: z.string(),
  quality: z.number().int().min(1).max(100).optional(),
});
/** Tile pyramid descriptor (SPEC §2.1, §4). */
export type PanoramaBubbleTilePyramid = Readonly<{
  readonly tileSize: number;
  readonly levels: number;
  readonly format: string;
  readonly quality?: number;
}>;

/** Project metadata block inside `stations.json` (SPEC §3). */
export type PanoramaBubbleProjectMeta = Readonly<{
  readonly id: string;
  readonly name: string;
  readonly crs: string;
  readonly epoch?: string;
  readonly boundingBox: Readonly<{
    readonly min: readonly [number, number, number];
    readonly max: readonly [number, number, number];
  }>;
}>;

/** Station pose in surveying frame (SPEC §4). */
export type PanoramaBubblePose = Readonly<{
  readonly translation: readonly [number, number, number];
  readonly rotation: readonly [number, number, number, number];
  readonly frame: "surveying";
}>;

/** RGB equirect descriptor (SPEC §4). */
export type PanoramaBubbleEquirectDescriptor = Readonly<{
  readonly width: number;
  readonly height: number;
  readonly north: "+Y";
  readonly hemisphere: "full" | "upper" | "lower";
  readonly columnZeroAzimuth: number;
  readonly rowZeroElevation?: number;
  readonly rowMaxElevation?: number;
  readonly tilePyramid: PanoramaBubbleTilePyramid;
}>;

/** RGB-packed-mm depth descriptor (SPEC §4, §6). */
export type PanoramaBubbleDepthDescriptor = Readonly<{
  readonly width: number;
  readonly height: number;
  readonly encoding: "rgb-packed-mm-png";
  readonly channels: 3;
  readonly min: number;
  readonly max: number;
  readonly units: "meters";
  readonly scaleMmPerUnit: 1000;
  readonly invalidValue: 0;
  readonly sampling: "NEAREST";
  readonly tilePyramid: PanoramaBubbleTilePyramid;
}>;

/** Synthetic-ray alignment proof metrics (SPEC §4, §8.3). */
export type PanoramaBubbleAlignmentProof = Readonly<{
  readonly syntheticRayCount: number;
  readonly syntheticRayMedianErrorMeters: number;
  readonly syntheticRayMaxErrorMeters: number;
  readonly syntheticRayMedianPixelError: number;
  readonly syntheticRayMaxPixelError: number;
}>;

/** Per-station entry in `stations.json` (SPEC §3). */
export type PanoramaBubbleStationEntry = Readonly<{
  readonly id: string;
  readonly name: string;
  readonly position: readonly [number, number, number];
  readonly quaternion: readonly [number, number, number, number];
  readonly scannerHeight: number;
  readonly manifestUrl: string;
  readonly previewUrl: string;
  readonly equirectMetadataUrl: string;
  readonly depthMetadataUrl: string;
  readonly neighbors: readonly string[];
  readonly floor?: number;
  readonly level?: string;
}>;

/** Project-level `stations.json` (SPEC §3). */
export type PanoramaBubbleStationsManifest = Readonly<{
  readonly $schema?: string;
  readonly schemaVersion: 1;
  readonly kind: "panorama-bubble-v1";
  readonly project: PanoramaBubbleProjectMeta;
  readonly stations: readonly PanoramaBubbleStationEntry[];
}>;

/** Per-station `manifest.json` (SPEC §4). */
export type PanoramaBubbleStationManifest = Readonly<{
  readonly $schema?: string;
  readonly schemaVersion: 1;
  readonly kind: "panorama-bubble-v1";
  readonly stationId: string;
  readonly pose: PanoramaBubblePose;
  readonly equirect: PanoramaBubbleEquirectDescriptor;
  readonly depth: PanoramaBubbleDepthDescriptor;
  readonly alignment: Readonly<{
    readonly panoramaToDepth: "identical-projection";
    readonly proof: PanoramaBubbleAlignmentProof;
  }>;
  readonly scanner?: Readonly<{
    readonly vendor?: string;
    readonly model?: string;
    readonly firmware?: string;
    readonly scanTimestamp?: string;
  }>;
}>;

/** Zod schema for project-level `stations.json` (SPEC §3). */
export const PanoramaBubbleStationsManifestSchema = z.object({
  $schema: z.string().optional(),
  schemaVersion: z.literal(1),
  kind: z.literal("panorama-bubble-v1"),
  project: z.object({
    id: z.string(),
    name: z.string(),
    crs: z.string(),
    epoch: z.string().optional(),
    boundingBox: z.object({
      min: vec3Schema,
      max: vec3Schema,
    }),
  }),
  stations: z
    .array(
      z.object({
        id: z.string().regex(/^[a-z0-9][a-z0-9\-]{0,62}$/),
        name: z.string(),
        position: vec3Schema,
        quaternion: quaternionSchema,
        scannerHeight: z.number().positive(),
        manifestUrl: z.string(),
        previewUrl: z.string(),
        equirectMetadataUrl: z.string(),
        depthMetadataUrl: z.string(),
        neighbors: z.array(z.string()),
        floor: z.number().int().optional(),
        level: z.string().optional(),
      }),
    )
    .min(1),
});

/** Zod schema for per-station `manifest.json` (SPEC §4). */
export const PanoramaBubbleStationManifestSchema = z.object({
  $schema: z.string().optional(),
  schemaVersion: z.literal(1),
  kind: z.literal("panorama-bubble-v1"),
  stationId: z.string().regex(/^[a-z0-9][a-z0-9\-]{0,62}$/),
  pose: z.object({
    translation: vec3Schema,
    rotation: quaternionSchema,
    frame: z.literal("surveying"),
  }),
  equirect: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    north: z.literal("+Y"),
    hemisphere: z.enum(["full", "upper", "lower"]),
    columnZeroAzimuth: z.number(),
    rowZeroElevation: z.number().optional(),
    rowMaxElevation: z.number().optional(),
    tilePyramid: PanoramaBubbleTilePyramidSchema,
  }),
  depth: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    encoding: z.literal("rgb-packed-mm-png"),
    channels: z.literal(3),
    min: z.number().min(0),
    max: z.number().min(0).max(16777.215),
    units: z.literal("meters"),
    scaleMmPerUnit: z.literal(1000),
    invalidValue: z.literal(0),
    sampling: z.literal("NEAREST"),
    tilePyramid: PanoramaBubbleTilePyramidSchema,
  }),
  alignment: z.object({
    panoramaToDepth: z.literal("identical-projection"),
    proof: z.object({
      syntheticRayCount: z.number().int().positive(),
      syntheticRayMedianErrorMeters: z.number().min(0),
      syntheticRayMaxErrorMeters: z.number().min(0),
      syntheticRayMedianPixelError: z.number().min(0),
      syntheticRayMaxPixelError: z.number().min(0),
    }),
  }),
  scanner: z
    .object({
      vendor: z.string().optional(),
      model: z.string().optional(),
      firmware: z.string().optional(),
      scanTimestamp: z.string().optional(),
    })
    .optional(),
});

// ---------------------------------------------------------------------------
// Sensor stream manifests (sensor-stream-frame-v1)
// ---------------------------------------------------------------------------

/** Manifest for sensor-stream-frame-v1 artifacts (4D / time-series). */
export const SensorManifestSchema = z.object({
  kind: z.literal("sensor-stream-frame-v1"),
  streamNdjsonUrl: z.string(),
  manifestUrl: z.string().nullable().optional(),
  frameCount: z.number(),
  timeRange: z.object({
    start: z.string(),
    end: z.string(),
    durationSeconds: z.number().nullable().optional(),
  }),
  sensorIds: z.array(z.string()),
  bbox: z.array(z.number()).nullable().optional(),
  qualityDistribution: z
    .object({
      good: z.number(),
      degraded: z.number(),
      bad: z.number(),
      unknown: z.number(),
    })
    .nullable()
    .optional(),
  valueRange: z
    .object({ min: z.number(), max: z.number() })
    .nullable()
    .optional(),
  buckets: z
    .array(
      z.object({
        t: z.string(),
        good: z.number(),
        degraded: z.number(),
        bad: z.number(),
        unknown: z.number(),
      }),
    )
    .nullable()
    .optional(),
  monotonicTimestamps: z.boolean().nullable().optional(),
  mode: z.enum(["replay", "live"]).nullable().optional(),
  liveExcludedFromQc: z.boolean().nullable().optional(),
});
export type SensorManifest = z.infer<typeof SensorManifestSchema>;

// ---------------------------------------------------------------------------
// OGC Features snapshot manifests (ogc-features-snapshot)
// ---------------------------------------------------------------------------

/** Manifest for ogc-features-snapshot artifacts (paged OGC API Features). */
export const FeaturesManifestSchema = z.object({
  kind: z.literal("ogc-features-snapshot"),
  pmtilesUrl: z.string().nullable().optional(),
  geojsonUrl: z.string().nullable().optional(),
  manifestUrl: z.string().nullable().optional(),
  crs: z.string(),
  crsIn: z.string().nullable().optional(),
  crsOut: z.string().nullable().optional(),
  mode: z.enum(["replay", "live"]),
  featureCount: z.number(),
  geometryTypes: z.array(z.string()),
  pageCount: z.number().nullable().optional(),
  paginationCompleted: z.boolean(),
  qcExcluded: z.boolean(),
  archiveSizeBytes: z.number().nullable().optional(),
});
export type FeaturesManifest = z.infer<typeof FeaturesManifestSchema>;

// ---------------------------------------------------------------------------
// Kind -> manifest schema lookup
// ---------------------------------------------------------------------------

/**
 * Map every `ArtifactKind` to the manifest schema that validates its
 * accompanying sidecar. `null` means the artifact has no manifest beyond the
 * `HandoffArtifact` record itself (e.g. `format-hint` is a pointer-only
 * artifact, `mosaic-json` is the manifest itself, etc.).
 *
 * Tests snapshot the keys of this map to lock the kind list against
 * accidental drift.
 */
export const MANIFEST_BY_KIND = {
  pmtiles: Map2DManifestSchema,
  "pmtiles-vector": Map2DManifestSchema,
  "pmtiles-raster": Map2DManifestSchema,
  cog: Map2DManifestSchema,
  geojson: Map2DManifestSchema,
  flatgeobuf: Map2DManifestSchema,
  geoparquet: Map2DManifestSchema,
  "mosaic-json": null,
  glb: ThreeDManifestSchema,
  "3dtiles": ThreeDManifestSchema,
  "cityjson-3dtiles": ThreeDManifestSchema,
  "quantized-mesh": ThreeDManifestSchema,
  copc: ThreeDManifestSchema,
  "potree-v2-octree": ThreeDManifestSchema,
  "dem-contours": ProfileManifestSchema,
  "panorama-pose-v1": PanoramaManifestSchema,
  "panorama-bubble-v1": PanoramaBubbleStationsManifestSchema,
  "sensor-stream-frame-v1": SensorManifestSchema,
  "ogc-features-snapshot": FeaturesManifestSchema,
  "format-hint": null,
  dxf: Map2DManifestSchema,
} as const satisfies Record<ArtifactKind, z.ZodTypeAny | null>;

export type ManifestByKind = typeof MANIFEST_BY_KIND;
